import React, { useMemo } from 'react';
import { Card, theme } from 'antd';
import { Line } from 'react-chartjs-2';
import type { ChartDataset } from 'chart.js';

import Colors from '~library/prerenderTheme/colors';
import { numberFormatter } from '~library/numbers';
import { getXAxisConfig } from '../../../dashboard-v3/charts';
import ChartCardDoubleLayout from '../../../dashboard-v3/components/ChartCardDoubleLayout';
import { AiInsightsEmptyState, useAiCrawlerLineup } from '../../shared';
import { formatDiscoverabilityPercentage } from '../formatDiscoverabilityPercentage';

import type { DiscoverabilityChartResponse } from '../api/discoverabilityChartsApi';
import parseChartResponseData, {
  type ChartSeries,
  type ChartSeriesProvider,
} from '../parseChartResponseData';
import CrawlerActivityChartLegend, {
  type CrawlerActivityLegendItem,
  ALL_CRAWLERS_LABEL,
} from './CrawlerActivityChartLegend';
import type { DiscoverabilityChartToggleAction } from '../DiscoverabilityAnalyticsEvents';

export type CrawlerActivityChartValueFormat = 'integer' | 'percent';

type CrawlerActivityChartProps = {
  valueFormat: CrawlerActivityChartValueFormat;
  data: DiscoverabilityChartResponse | undefined;
  // All Crawlers line values, scoped by the parent to the visible crawlers
  // (PRE-3094). When absent (nothing hidden) the main response's `all` is
  // plotted; an empty array plots no aggregate line.
  allCrawlersValues?: number[];
  isLoading: boolean;
  // Account has never been visited by an AI crawler (the KPI response's
  // `hasEverHadCrawlerActivity` is false) — computed once in
  // DiscoverabilityView and shared with the KPIs and Pages sections so all
  // three speak with one voice. The backend returns a zero-filled date grid for
  // these accounts, so `isEmpty(series)` never fires on its own and the chart
  // would otherwise draw a flat-zero line beside the "Waiting" cards (PRE-3098).
  waitingForCrawlerRequests?: boolean;
  hiddenSeries: ReadonlySet<string>;
  onSeriesToggled?: (series: string, action: DiscoverabilityChartToggleAction) => void;
};

const ALL_CRAWLERS_COLOR = Colors.Gray4;

const Y_AXIS_TICK_COUNT = 6;

// FIXME: no exact token — the plot area needs a fixed floor so the canvas keeps
// its aspect ratio next to the legend column; no antd size token reaches 280px.
const MAIN_CHART_MIN_HEIGHT = 280;

const PROVIDER_COLORS: Record<ChartSeriesProvider, string> = {
  ChatGPT: Colors.Purple5,
  Claude: Colors.Cyan6,
  Gemini: Colors.GeekBlue,
  Perplexity: Colors.Magenta5,
  Amazon: Colors.GeekBlue,
  Other: Colors.Neutral,
};

function formatTooltipValue(value: number, format: CrawlerActivityChartValueFormat): string {
  if (format === 'percent') return formatDiscoverabilityPercentage(value);
  return numberFormatter.format(value);
}

function buildLegendItems(series: ChartSeries): CrawlerActivityLegendItem[] {
  return series.perCrawler.map((entry) => ({
    provider: entry.provider,
    color: PROVIDER_COLORS[entry.provider],
  }));
}

// A crawl request is a discrete event, so its axis must never read 0.5 requests
// — the 7-day rolling average makes the plotted values fractional (PRE-3093).
// `count` would force exactly that many evenly-spaced ticks (fractional steps on
// a small range), so the integer axis asks for integer steps via `precision: 0`
// and caps the tick count instead. Percentages are legitimately fractional and
// keep the fixed tick count.
function yAxisTickConfig(format: CrawlerActivityChartValueFormat) {
  if (format === 'percent') return { count: Y_AXIS_TICK_COUNT };
  return { precision: 0, maxTicksLimit: Y_AXIS_TICK_COUNT };
}

function isEmpty(series: ChartSeries): boolean {
  return series.dates.length === 0;
}

function CrawlerActivityChart({
  valueFormat,
  data,
  allCrawlersValues,
  isLoading,
  waitingForCrawlerRequests = false,
  hiddenSeries,
  onSeriesToggled,
}: CrawlerActivityChartProps) {
  const { token } = theme.useToken();
  const lineup = useAiCrawlerLineup();

  const series = useMemo<ChartSeries>(
    () => (data ? parseChartResponseData(lineup, data) : { dates: [], perCrawler: [], allCrawlers: [] }),
    [data, lineup]
  );

  const legendItems = useMemo(() => buildLegendItems(series), [series]);

  const itemsVisibility = useMemo(
    () => [ALL_CRAWLERS_LABEL, ...legendItems.map((item) => item.provider)]
      .map((name) => !hiddenSeries.has(name)),
    [legendItems, hiddenSeries]
  );

  const handleItemClicked = (index: number) => {
    const seriesName = index === 0 ? ALL_CRAWLERS_LABEL : legendItems[index - 1]?.provider;
    if (!seriesName) return;

    const wasVisible = !hiddenSeries.has(seriesName);
    const action: DiscoverabilityChartToggleAction = wasVisible ? 'hide' : 'show';

    onSeriesToggled?.(seriesName, action);
  };

  const allCrawlersDataset: ChartDataset<'line'> = useMemo(
    () => ({
      label: ALL_CRAWLERS_LABEL,
      data: allCrawlersValues ?? series.allCrawlers,
      borderColor: ALL_CRAWLERS_COLOR,
      backgroundColor: ALL_CRAWLERS_COLOR,
      borderDash: [4, 4],
      borderWidth: 1,
      tension: 0.4,
      pointRadius: 3,
      pointHoverRadius: 5,
      pointBackgroundColor: token.colorBgContainer,
      pointBorderColor: ALL_CRAWLERS_COLOR,
      pointBorderWidth: 1,
      hidden: hiddenSeries.has(ALL_CRAWLERS_LABEL),
    }),
    [allCrawlersValues, series.allCrawlers, hiddenSeries, token.colorBgContainer]
  );

  const crawlerDatasets: ChartDataset<'line'>[] = useMemo(
    () =>
      series.perCrawler.map((entry) => {
        const color = PROVIDER_COLORS[entry.provider];
        return {
          label: entry.provider,
          data: entry.values,
          borderColor: color,
          backgroundColor: color,
          borderWidth: 1,
          tension: 0.4,
          pointRadius: 3,
          pointHoverRadius: 5,
          pointBackgroundColor: token.colorBgContainer,
          pointBorderColor: color,
          pointBorderWidth: 1,
          hidden: hiddenSeries.has(entry.provider),
        };
      }),
    [series.perCrawler, hiddenSeries, token.colorBgContainer]
  );

  const cardStyle: React.CSSProperties = {
    borderRadius: token.borderRadiusLG,
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
  };

  if (isLoading) {
    return <Card loading style={{ ...cardStyle, height: '100%' }} />;
  }

  if (waitingForCrawlerRequests || isEmpty(series)) {
    return (
      <Card style={cardStyle}>
        <AiInsightsEmptyState
          headline="Waiting for AI Crawler requests"
          body="Once AI Crawlers visit your site, your data will appear here."
        />
      </Card>
    );
  }

  return (
    <Card style={cardStyle} styles={{ body: { paddingTop: token.padding } }}>
      <ChartCardDoubleLayout
        MainChart={
          <div style={{ minHeight: MAIN_CHART_MIN_HEIGHT }}>
            <Line
              data={{
                labels: series.dates,
                datasets: [...crawlerDatasets, allCrawlersDataset],
              }}
              options={{
                // 'nearest' (full xy distance) keeps the tooltip on the single
                // hovered line; 'index' would list every crawler at that x.
                interaction: { intersect: false, mode: 'nearest' },
                scales: {
                  x: getXAxisConfig('day'),
                  y: {
                    type: 'linear',
                    beginAtZero: true,
                    min: 0,
                    ticks: {
                      ...yAxisTickConfig(valueFormat),
                      callback: (value) => formatTooltipValue(Number(value), valueFormat),
                    },
                    grid: {
                      color: token.colorBorderSecondary,
                      tickBorderDash: [2, 4],
                    },
                  },
                },
                plugins: {
                  tooltip: {
                    callbacks: {
                      // Chart.js defaults the tooltip swatch to the *point*
                      // style for line datasets, which here is a white-filled
                      // hollow circle — so the swatch reads as just a colored
                      // ring. Paint it solid with the line color instead.
                      labelColor: (ctx) => {
                        const color = ctx.dataset.borderColor as string;
                        return { backgroundColor: color, borderColor: color };
                      },
                      label: (ctx) =>
                        `${ctx.dataset.label}: ${formatTooltipValue(Number(ctx.parsed.y ?? 0), valueFormat)}`,
                    },
                  },
                },
              }}
            />
          </div>
        }
        SideChart={
          <CrawlerActivityChartLegend
            items={legendItems}
            allCrawlersColor={ALL_CRAWLERS_COLOR}
            itemsVisibility={itemsVisibility}
            onItemClicked={handleItemClicked}
          />
        }
      />
    </Card>
  );
}

export default CrawlerActivityChart;
